/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */
/* eslint-disable camelcase */
// ==============================================
// ==================Import======================
// ==============================================

// ===================Dependancies================
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

// =================React hook dep================
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// ==================Style&IMG===================
import './style.scss';

// ===================Actions====================

import {
  setSaveAntennaList,
  setSaveFrequencyStandardSensorList,
  setSaveHumiditySensorList,
  setSavePressureSensorList,
  setSaveRadomeList,
  setSaveReceiverList,
  setSaveSessionViewData,
  setSaveTemperatureSensorList,
  setSaveWaterVaporSensorList,
} from '../../../actions/metadata';

// ==================Composants===================
import SessionView from './SessionView';
import ListView from './ListView';
import OtherInstruments from './OtherInstruments';

function Instrumentation({ fullData }) {
  // ===============HOOKS===========
  const dispatch = useDispatch();
  const sessionViewData = useSelector(
    (state) => state.metadata.data.instrumentation.sessionViewData,
  );
  const antennaListState = useSelector((state) => state.metadata.data.instrumentation.antennaList);
  const radomeListState = useSelector((state) => state.metadata.data.instrumentation.radomeList);
  const receiverListState = useSelector(
    (state) => state.metadata.data.instrumentation.receiverList,
  );
  const frequencyStandardSensorListState = useSelector(
    (state) => state.metadata.data.instrumentation.frequencyStandardSensorList,
  );
  const humiditySensorListState = useSelector(
    (state) => state.metadata.data.instrumentation.humiditySensorList,
  );
  const pressureSensorListState = useSelector(
    (state) => state.metadata.data.instrumentation.pressureSensorList,
  );
  const temperatureSensorListState = useSelector(
    (state) => state.metadata.data.instrumentation.temperatureSensorList,
  );
  const waterVaporSensorlistState = useSelector(
    (state) => state.metadata.data.instrumentation.waterVaporSensorlist,
  );

  // ============Functions===================

  const recordsToInject = fullData[0].station_items || [];

  const relevantTypes = ['receiver', 'antenna', 'radome'];

  function collectDates(recs) {
    const set = new Set();
    recs
      .filter((r) => relevantTypes.includes(r.item?.item_type?.name))
      .forEach((r) => {
        if (r.date_from) set.add(r.date_from);
        if (r.date_to) set.add(r.date_to);
      });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b));
  }

  function buildTimeline(records, currentDate) {
    // 1) Grouped by type by making an array for each type antenna, receiver ....
    // taking only the types that interest us in relevantTypes.
    const byType = records
      .filter((r) => relevantTypes.includes(r.item?.item_type?.name))
      .reduce((acc, r) => {
        const t = r.item.item_type.name;
        (acc[t] = acc[t] || []).push(r);
        return acc;
      }, {});

    // 2) Variable to initialize objects with key-value pairs. If no value, initialize with {}
    const state = relevantTypes.reduce((acc, t) => {
      acc[t] = {};
      return acc;
    }, {});

    // 3) Creation of an array containing all the dates from the different targeted types
    const allDates = collectDates(records);
    const timeline = [];
    let lastContent = null;

    for (let i = 0; i < allDates.length; i += 1) {
      const datefrom = allDates[i];
      const dateto = (i < allDates.length - 1 ? allDates[i + 1] : currentDate);

      // 4) Update state for each type when a record starts
      for (const [type, arr] of Object.entries(byType)) {
        const rec = arr.find((r) => r.date_from === datefrom);
        if (rec) {
          const attrs = rec.item.item_attributes || [];
          state[type] = Object.fromEntries(
            attrs.map((a) => [
              a.attribute.name,
              a.value_varchar ?? a.value_numeric,
            ]),
          );
        }
      }
      // 5) Check values for each entry if no value added N/C and creation of object content
      const content = {
        antenna: state.antenna?.antenna_type ?? 'N/C',
        antenna_serial_number: state.antenna?.serial_number ?? 'N/C',
        radome: state.radome?.radome_type ?? 'N/C',
        receiver: state.receiver?.receiver_type ?? 'N/C',
        receiver_serial_number: state.receiver?.serial_number ?? 'N/C',
        firmware: state.receiver?.firmware_version ?? 'N/C',
        satellites_system: state.receiver?.satellite_system ?? 'N/C',
      };

      // 6) Adding all pre-calculated and sorted elements to the timeline array
      if (!lastContent || JSON.stringify(content) !== JSON.stringify(lastContent)) {
        timeline.push({
          datefrom,
          dateto,
          ...content,
          currentDate,
        });
        lastContent = content;
      }
    }

    return timeline;
  }

  function formatSQLDate(date) {
    const pad2 = (n) => String(n).padStart(2, '0');
    const Y = date.getFullYear();
    const M = pad2(date.getMonth() + 1);
    const D = pad2(date.getDate());
    const h = pad2(date.getHours());
    const m = pad2(date.getMinutes());
    const s = pad2(date.getSeconds());
    return `${Y}-${M}-${D} ${h}:${m}:${s}`;
  }

  const now = new Date();
  const currentDateSessionView = formatSQLDate(now);

  // save actual date in specify format
  function currentDateFormat() {
    const today = new Date();
    let dd = today.getDate();
    let mm = today.getMonth();
    let HH = today.getHours();
    let MM = today.getMinutes();
    let SS = today.getSeconds();
    const yyyy = today.getFullYear();
    if (dd < 10) {
      dd = `0${dd}`;
    }
    if (mm < 10) {
      mm = `0${mm}`;
    }
    if (HH < 10) {
      HH = `0${HH}`;
    }
    if (MM < 10) {
      MM = `0${MM}`;
    }
    if (SS < 10) {
      SS = `0${SS}`;
    }
    const todayDate = `${yyyy}-${mm}-${dd} ${HH}:${MM}:${SS}`;
    return todayDate;
  }
  // function to sort atenna, radome, ....
  function sortEquipment() {
    let precReceiverDate = '';
    let precAntennaDate = '';
    let precRadomeDate = '';
    const data = fullData;

    for (let i = 0; i < data.length; i += 1) {
      for (let j = 0; j < data[i].station_items.length; j += 1) {
        if (data[i].station_items[j].item.item_type.name === 'antenna') {
          if (precAntennaDate !== '') {
            const d1 = new Date(precAntennaDate.replace(/-/g, '/'));
            const d2 = new Date(data[i].station_items[j].item.item_attributes[0].date_from.replace(/-/g, '/'));
            if (d1.getTime() > d2.getTime()) {
              const tempData = data[i].station_items[j];
              data[i].station_item[j] = data[i].station_items[j - 1];
              data[i].station_items[j - 1] = tempData;
            }
          }
          precAntennaDate = data[i].station_items[j].item.item_attributes[0].date_from;
        }
        if (data[i].station_items[j].item.item_type.name === 'receiver') {
          if (precReceiverDate !== '') {
            const d1 = new Date(precReceiverDate.replace(/-/g, '/'));
            const d2 = new Date(data[i].station_items[j].item.item_attributes[0].date_from.replace(/-/g, '/'));
            if (d1.getTime() > d2.getTime()) {
              const tempData = data[i].station_items[j];
              data[i].station_item[j] = data[i].station_items[j - 1];
              data[i].station_items[j - 1] = tempData;
            }
          }
          precReceiverDate = data[i].station_items[j].item.item_attributes[0].date_from;
        }
        if (data[i].station_items[j].item.item_type.name === 'radome') {
          if (precRadomeDate !== '') {
            const d1 = new Date(precRadomeDate.replace(/-/g, '/'));
            const d2 = new Date(data[i].station_items[j].item.item_attributes[0].date_from.replace(/-/g, '/'));
            if (d1.getTime() > d2.getTime()) {
              const tempData = data[i].station_items[j];
              data[i].station_item[j] = data[i].station_items[j - 1];
              data[i].station_items[j - 1] = tempData;
            }
          }
          precRadomeDate = data[i].station_items[j].item.item_attributes[0].date_from;
        }
      }
    }
    return data;
  }

  // function to create list and array for each type of equipement and save all data in state

  function displayEquipment() {
    const arrTemp = [];
    const antennaList = [];
    let antennaDateto = '';
    let antennaComment = '';
    let antennaName = '';
    let antennaDatefrom = '';
    let antennaIgs = '';
    let antennaModel = '';
    let antennaSerialNumber = '';
    let antennaReferencePoint = '';
    let antennaMarkerArpUpEcc = '';
    let antennaMarkerNorthEcc = '';
    let antennaMarkerEastEcc = '';
    let antennaAlignement = '';
    let antennaCableType = '';
    let antennaCableLength = '';
    let radomeComment = '';
    let radomeDateto = '';
    let radomeName = '';
    let receiverDateto = '';
    let receiverComment = '';
    let receiverName = '';
    let receiverDatefrom = '';
    let receiverIgs = '';
    let receiverModel = '';
    let receiverSerialNumber = '';
    let receiverFirmware = '';
    let receiverSatellites = '';
    let receiverElevation = '';
    let receiverTemperature = '';
    let receiverFrequency = '';
    let receiverDataSampling = '';
    // let dateto = '';
    // let datefrom = '';
    let sensor_dateto_humidity = '';
    let sensor_comment_humidity = '';
    let sensor_model_humidity = '';
    let sensor_datefrom_humidity = '';
    let sensor_manufacturer_humidity = '';
    let sensor_serial_number_humidity = '';
    let sensor_data_sampling_interval_humidity = '';
    let sensor_accuracy_humidity = '';
    let sensor_height_diff_to_ant_humidity = '';
    let sensor_aspiration_humidity = '';
    let sensor_calibration_date_humidity = '';
    let sensor_dateto_pressure = '';
    let sensor_comment_pressure = '';
    let sensor_datefrom_pressure = '';
    let sensor_model_pressure = '';
    let sensor_manufacturer_pressure = '';
    let sensor_serial_number_pressure = '';
    let sensor_data_sampling_interval_pressure = '';
    let sensor_accuracy_pressure = '';
    let sensor_height_diff_to_ant_pressure = '';
    let sensor_calibration_date_pressure = '';
    let sensor_aspiration_pressure = '';
    let sensor_dateto_temperature = '';
    let sensor_comment_temperature = '';
    let sensor_model_temperature = '';
    let sensor_datefrom_temperature = '';
    let sensor_manufacturer_temperature = '';
    let sensor_serial_number_temperature = '';
    let sensor_data_sampling_interval_temperature = '';
    let sensor_accuracy_temperature = '';
    let sensor_height_diff_to_ant_temperature = '';
    let sensor_calibration_date_temperature = '';
    let sensor_aspiration_temperature = '';
    let sensor_distance_to_antenna_temperature = '';
    let sensor_dateto_radiometer = '';
    let sensor_comment_radiometer = '';
    let sensor_water_vapor_radiometer = '';
    let sensor_datefrom_radiometer = '';
    let sensor_manufacturer_radiometer = '';
    let sensor_serial_number_radiometer = '';
    let sensor_height_diff_to_ant_radiometer = '';
    let sensor_calibration_date_radiometer = '';
    let sensor_distance_to_antenna_radiometer = '';
    let sensor_datefrom_frequency = '';
    let sensor_dateto_frequency = '';
    let sensor_comment_frequency = '';
    let sensor_standard_type_frequency = '';
    let sensor_input_frequency_frequency = '';
    const receiverList = [];
    const radomeList = [];
    const humiditySensorList = [];
    const pressureSensorList = [];
    const temperatureSensorList = [];
    const waterVaporSensorlist = [];
    const frequencyStandardSensorList = [];
    // let precDate = '';
    let count = 0;
    const currentDate = currentDateFormat();
    const dataToDisplay = sortEquipment();
    for (let i = 0; i < dataToDisplay.length; i += 1) {
      for (let j = 0; j < dataToDisplay[i].station_items.length; j += 1) {
        if (dataToDisplay[i].station_items[j].item.item_type.name === 'antenna') {
          if (dataToDisplay[i].station_items[j].item.item_attributes[0].date_to === null) {
            antennaDateto = currentDate;
          }
          else {
            antennaDateto = dataToDisplay[i].station_items[j].item.item_attributes[0].date_to;
          }
          antennaComment = dataToDisplay[i].station_items[j].item.comment;
          for (let k = 0; k < dataToDisplay[i].station_items[j].item.item_attributes.length; k += 1) {
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'antenna_type') {
              antennaName = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              antennaDatefrom = dataToDisplay[i].station_items[j].item.item_attributes[k].date_from;
              antennaIgs = dataToDisplay[i].station_items[j].item.item_attributes[k].igs_defined;
              antennaModel = dataToDisplay[i].station_items[j].item.item_attributes[k].model;
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].date_to === null) {
                antennaDateto = currentDate;
              }
              else {
                antennaDateto = dataToDisplay[i].station_items[j].item.item_attributes[k].date_to;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'serial_number') {
              antennaSerialNumber = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'antenna_reference_point') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaReferencePoint = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaReferencePoint = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaReferencePoint = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'marker_arp_up_ecc') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaMarkerArpUpEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaMarkerArpUpEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaMarkerArpUpEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'marker_arp_north_ecc') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaMarkerNorthEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaMarkerNorthEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaMarkerNorthEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'marker_arp_east_ecc') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaMarkerEastEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaMarkerEastEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaMarkerEastEcc = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'alignment_from_true_n') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaAlignement = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaAlignement = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaAlignement = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'antenna_cable_type') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaCableType = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaCableType = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaCableType = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'antenna_cable_length') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                antennaCableLength = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                antennaCableLength = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                antennaCableLength = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
          }
          antennaList.push({
            currentDate: currentDate,
            currentDateId: uuidv4(),
            datefrom: antennaDatefrom,
            datefromId: uuidv4(),
            dateto: antennaDateto,
            datetoId: uuidv4(),
            antenna: antennaName,
            antennaId: uuidv4(),
            antenna_serial_number: antennaSerialNumber,
            antenna_serial_number_id: uuidv4(),
            igs: antennaIgs,
            model: antennaModel,
            antenna_reference_point: antennaReferencePoint,
            antenna_reference_point_id: uuidv4(),
            marker_arp_up_ecc: antennaMarkerArpUpEcc,
            marker_arp_up_ecc_id: uuidv4(),
            marker_arp_north_ecc: antennaMarkerNorthEcc,
            marker_arp_north_ecc_id: uuidv4(),
            marker_arp_east_ecc: antennaMarkerEastEcc,
            marker_arp_east_ecc_id: uuidv4(),
            alignment_from_true_n: antennaAlignement,
            alignment_from_true_n_id: uuidv4(),
            antenna_cable_type: antennaCableType.toString(),
            antenna_cable_type_id: uuidv4(),
            antenna_cable_length: antennaCableLength,
            antenna_cable_length_id: uuidv4(),
            comment: antennaComment,
            commentId: uuidv4(),
          });
          for (let l = 0; l < dataToDisplay[i].station_items.length; l += 1) {
            if (dataToDisplay[i].station_items[l].item.item_type.name === 'radome') {
              radomeComment = dataToDisplay[i].station_items[l].item.comment;
              for (let k = 0; k < dataToDisplay[i].station_items[l].item.item_attributes.length; k += 1) {
                if (dataToDisplay[i].station_items[l].item.item_attributes[k].attribute.name === 'radome_type') {
                  if (dataToDisplay[i].station_items[l].item.item_attributes[k].date_to === null) {
                    radomeDateto = currentDate;
                  }
                  else {
                    radomeDateto = dataToDisplay[i].station_items[l].item.item_attributes[k].date_to;
                  }
                  if ((dataToDisplay[i].station_items[l].item.item_attributes[k].date_from === antennaDatefrom) && (radomeDateto === antennaDateto)) {
                    radomeName = dataToDisplay[i].station_items[l].item.item_attributes[k].value_varchar;
                    radomeList.push({
                      currentDate: currentDate,
                      currentDateId: uuidv4(),
                      datefrom: dataToDisplay[i].station_items[l].item.item_attributes[k].date_from,
                      datefromId: uuidv4(),
                      dateto: radomeDateto,
                      dateotId: uuidv4(),
                      radome: radomeName,
                      radomeId: uuidv4(),
                      igs: dataToDisplay[i].station_items[l].item.item_attributes[k].igs_defined,
                      description: dataToDisplay[i].station_items[l].item.item_attributes[k].description,
                      descriptionId: uuidv4(),
                      comment: radomeComment,
                      commentId: uuidv4(),
                    });
                  }
                }
              }
            }
          }
          for (let l = 0; l < dataToDisplay[i].station_items.length; l += 1) {
            if (dataToDisplay[i].station_items[l].item.item_type.name === 'receiver') {
              receiverComment = dataToDisplay[i].station_items[l].item.comment;
              for (let m = 0; m < dataToDisplay[i].station_items[l].item.item_attributes.length; m += 1) {
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'receiver_type') {
                  receiverName = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                  receiverDatefrom = dataToDisplay[i].station_items[l].item.item_attributes[m].date_from;
                  receiverIgs = dataToDisplay[i].station_items[l].item.item_attributes[m].igs_defined;
                  receiverModel = dataToDisplay[i].station_items[l].item.item_attributes[m].model;
                  if (dataToDisplay[i].station_items[l].item.item_attributes[m].date_to === null) {
                    receiverDateto = currentDate;
                  }
                  else {
                    receiverDateto = dataToDisplay[i].station_items[l].item.item_attributes[m].date_to;
                  }
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'serial_number') {
                  receiverSerialNumber = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'firmware_version') {
                  receiverFirmware = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'satellite_system') {
                  receiverSatellites = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'elevation_cutoff_setting') {
                  if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar !== null) {
                    receiverElevation = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric !== null) {
                    receiverElevation = dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_date !== null) {
                    receiverElevation = dataToDisplay[i].station_items[l].item.item_attributes[m].value_date;
                  }
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'temperature_stabilization') {
                  if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar !== null) {
                    receiverTemperature = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric !== null) {
                    receiverTemperature = dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_date !== null) {
                    receiverTemperature = dataToDisplay[i].station_items[l].item.item_attributes[m].value_date;
                  }
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'input_frequency') {
                  if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar !== null) {
                    receiverFrequency = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric !== null) {
                    receiverFrequency = dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_date !== null) {
                    receiverFrequency = dataToDisplay[i].station_items[l].item.item_attributes[m].value_date;
                  }
                }
                if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'data_sampling_interval') {
                  if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar !== null) {
                    receiverDataSampling = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric !== null) {
                    receiverDataSampling = dataToDisplay[i].station_items[l].item.item_attributes[m].value_numeric;
                  }
                  else if (dataToDisplay[i].station_items[l].item.item_attributes[m].value_date !== null) {
                    receiverDataSampling = dataToDisplay[i].station_items[l].item.item_attributes[m].value_date;
                  }
                }
              }
              if (count === 0) {
                receiverList.push({
                  currentDate: currentDate,
                  currentDateId: uuidv4(),
                  datefrom: receiverDatefrom,
                  datefromId: uuidv4(),
                  dateto: receiverDateto,
                  datetoId: uuidv4(),
                  receiver: receiverName,
                  receiverId: uuidv4(),
                  receiver_serial_number: receiverSerialNumber,
                  receiver_serial_number_id: uuidv4(),
                  firmware: receiverFirmware,
                  firmwareId: uuidv4(),
                  satellites_system: receiverSatellites,
                  satellites_system_id: uuidv4(),
                  igs: receiverIgs,
                  model: receiverModel,
                  receiver_elevation_cutoff_setting: receiverElevation,
                  receiver_elevation_cutoff_setting_id: uuidv4(),
                  receiver_temperature_stabilization: receiverTemperature,
                  receiver_temperature_stabilization_id: uuidv4(),
                  receiver_input_frequency: receiverFrequency,
                  receiver_input_frequency_id: uuidv4(),
                  receiver_data_sampling_interval: receiverDataSampling,
                  receiver_data_sampling_interval_id: uuidv4(),
                  comment: receiverComment,
                  commentId: uuidv4(),
                });
              }

              // const d1 = new Date(dataToDisplay[i].station_items[l].item.item_attributes[0].date_from.replace(/-/g, '/'));
              // const d2 = new Date(antennaDateto.replace(/-/g, '/'));
              // const d3 = new Date(receiverDateto.replace(/-/g, '/'));
              // const d4 = new Date(antennaDatefrom.replace(/-/g, '/'));
              // if ((d1.getTime() <= d2.getTime()) && (d3.getTime() >= d4.getTime())) {
              //   for (let m = 0; m < dataToDisplay[i].station_items[l].item.item_attributes.length; m += 1) {
              //     if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'receiver_type') {
              //       receiverName = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
              //       receiverDatefrom = dataToDisplay[i].station_items[l].item.item_attributes[m].date_from;
              //       if (dataToDisplay[i].station_items[l].item.item_attributes[m].date_to === null) {
              //         receiverDateto = currentDate;
              //       }
              //       else {
              //         receiverDateto = dataToDisplay[i].station_items[l].item.item_attributes[m].date_to;
              //       }
              //     }
              //     if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'serial_number') {
              //       receiverSerialNumber = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
              //     }
              //     if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'firmware_version') {
              //       receiverFirmware = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
              //     }
              //     if (dataToDisplay[i].station_items[l].item.item_attributes[m].attribute.name === 'satellite_system') {
              //       receiverSatellites = dataToDisplay[i].station_items[l].item.item_attributes[m].value_varchar;
              //     }
              //   }
              //   if (precDate === '') {
              //     const d5 = new Date(receiverDateto.replace(/-/g, '/'));
              //     const d6 = new Date(antennaDateto.replace(/-/g, '/'));
              //     if (d5.getTime() <= d6.getTime()) {
              //       dateto = receiverDateto;
              //     }
              //     else {
              //       dateto = antennaDateto;
              //     }
              //     arrTemp.push({
              //       datefrom: receiverDatefrom,
              //       dateto: dateto,
              //       antenna: antennaName,
              //       antenna_serial_number: antennaSerialNumber,
              //       radome: radomeName,
              //       receiver: receiverName,
              //       receiver_serial_number: receiverSerialNumber,
              //       firmware: receiverFirmware,
              //       satellites_system: receiverSatellites,
              //       currentDate: currentDate,
              //     });
              //     precDate = dateto;
              //   }
              //   else {
              //     const d7 = new Date(receiverDatefrom.replace(/-/g, '/'));
              //     const d8 = new Date(precDate.replace(/-/g, '/'));
              //     if (d7.getTime() < d8.getTime()) {
              //       datefrom = antennaDatefrom;
              //     }
              //     else {
              //       datefrom = receiverDatefrom;
              //     }
              //     const d9 = new Date(receiverDateto.replace(/-/g, '/'));
              //     const d10 = new Date(antennaDateto.replace(/-/g, '/'));
              //     if (d9.getTime() <= d10.getTime()) {
              //       dateto = receiverDateto;
              //     }
              //     else {
              //       dateto = antennaDateto;
              //     }
              //     arrTemp.push({
              //       datefrom: datefrom,
              //       dateto: dateto,
              //       antenna: antennaName,
              //       antenna_serial_number: antennaSerialNumber,
              //       radome: radomeName,
              //       receiver: receiverName,
              //       receiver_serial_number: receiverSerialNumber,
              //       firmware: receiverFirmware,
              //       satellites_system: receiverSatellites,
              //       currentDate: currentDate,
              //     });
              //     precDate = dateto;
              //   }
              // }
            }
          }
          count += 1;
        }
        if (dataToDisplay[i].station_items[j].item.item_type.name === 'humidity_sensor') {
          if (dataToDisplay[i].station_items[j].item.item_attributes[0].date_to === null) {
            sensor_dateto_humidity = currentDate;
          }
          else {
            sensor_dateto_humidity = dataToDisplay[i].station_items[j].item.item_attributes[0].date_to;
          }
          sensor_comment_humidity = dataToDisplay[i].station_items[j].item.comment;
          for (let k = 0; k < dataToDisplay[i].station_items[j].item.item_attributes.length; k += 1) {
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'sensor_model') {
              sensor_model_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              sensor_datefrom_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].date_from;
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].date_to === null) {
                sensor_dateto_humidity = currentDate;
              }
              else {
                sensor_dateto_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].date_to;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'manufacturer') {
              sensor_manufacturer_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'serial_number') {
              sensor_serial_number_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'data_sampling_interval') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_data_sampling_interval_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_data_sampling_interval_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_data_sampling_interval_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'accuracy') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_accuracy_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_accuracy_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_accuracy_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'height_diff_to_ant') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_height_diff_to_ant_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_height_diff_to_ant_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_height_diff_to_ant_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'calibration_date') {
              sensor_calibration_date_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'aspiration') {
              sensor_aspiration_humidity = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
          }
          humiditySensorList.push({
            currentDate: currentDate,
            currentDateId: uuidv4(),
            datefrom: sensor_datefrom_humidity,
            datefromId: uuidv4(),
            dateto: sensor_dateto_humidity,
            datetoId: uuidv4(),
            sensor_model: sensor_model_humidity,
            sensor_model_id: uuidv4(),
            sensor_manufacturer: sensor_manufacturer_humidity,
            sensor_manufacturer_id: uuidv4(),
            sensor_serial_number: sensor_serial_number_humidity,
            sensor_serial_number_id: uuidv4(),
            sensor_data_sampling_interval: sensor_data_sampling_interval_humidity,
            sensor_data_sampling_interval_id: uuidv4(),
            sensor_accuracy: sensor_accuracy_humidity,
            sensor_accuracy_id: uuidv4(),
            sensor_height_diff_to_ant: sensor_height_diff_to_ant_humidity,
            sensor_height_diff_to_ant_id: uuidv4(),
            sensor_calibration_date: sensor_calibration_date_humidity,
            sensor_calibration_date_id: uuidv4(),
            sensor_aspiration: sensor_aspiration_humidity,
            sensor_aspiration_id: uuidv4(),
            comment: sensor_comment_humidity,
            commentId: uuidv4(),
          });
        }
        if (dataToDisplay[i].station_items[j].item.item_type.name === 'pressure_sensor') {
          if (dataToDisplay[i].station_items[j].item.item_attributes[0].date_to === null) {
            sensor_dateto_pressure = currentDate;
          }
          else {
            sensor_dateto_pressure = dataToDisplay[i].station_items[j].item.item_attributes[0].date_to;
          }
          sensor_comment_pressure = dataToDisplay[i].station_items[j].item.comment;
          for (let k = 0; k < dataToDisplay[i].station_items[j].item.item_attributes.length; k += 1) {
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'sensor_model') {
              sensor_model_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              sensor_datefrom_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].date_from;
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].date_to === null) {
                sensor_dateto_pressure = currentDate;
              }
              else {
                sensor_dateto_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].date_to;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'manufacturer') {
              sensor_manufacturer_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'serial_number') {
              sensor_serial_number_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'data_sampling_interval') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_data_sampling_interval_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_data_sampling_interval_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_data_sampling_interval_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'accuracy') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_accuracy_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_accuracy_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_accuracy_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'height_diff_to_ant') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_height_diff_to_ant_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_height_diff_to_ant_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_height_diff_to_ant_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'calibration_date') {
              sensor_calibration_date_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'aspiration') {
              sensor_aspiration_pressure = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
          }
          pressureSensorList.push({
            currentDate: currentDate,
            currentDateId: uuidv4(),
            datefrom: sensor_datefrom_pressure,
            datefromId: uuidv4(),
            dateto: sensor_dateto_pressure,
            datetoId: uuidv4(),
            sensor_model: sensor_model_pressure,
            sensor_model_id: uuidv4(),
            sensor_manufacturer: sensor_manufacturer_pressure,
            sensor_manufacturer_id: uuidv4(),
            sensor_serial_number: sensor_serial_number_pressure,
            sensor_serial_number_id: uuidv4(),
            sensor_data_sampling_interval: sensor_data_sampling_interval_pressure,
            sensor_data_sampling_interval_id: uuidv4(),
            sensor_accuracy: sensor_accuracy_pressure,
            sensor_accuracy_id: uuidv4(),
            sensor_height_diff_to_ant: sensor_height_diff_to_ant_pressure,
            sensor_height_diff_to_ant_id: uuidv4(),
            sensor_calibration_date: sensor_calibration_date_pressure,
            sensor_calibration_date_id: uuidv4(),
            sensor_aspiration: sensor_aspiration_pressure,
            sensor_aspiration_id: uuidv4(),
            comment: sensor_comment_pressure,
            commentId: uuidv4(),
          });
        }
        if (dataToDisplay[i].station_items[j].item.item_type.name === 'temperature_sensor') {
          if (dataToDisplay[i].station_items[j].item.item_attributes[0].date_to === null) {
            sensor_dateto_temperature = currentDate;
          }
          else {
            sensor_dateto_temperature = dataToDisplay[i].station_items[j].item.item_attributes[0].date_to;
          }
          sensor_comment_temperature = dataToDisplay[i].station_items[j].item.comment;
          for (let k = 0; k < dataToDisplay[i].station_items[j].item.item_attributes.length; k += 1) {
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'sensor_model') {
              sensor_model_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              sensor_datefrom_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].date_from;
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].date_to === null) {
                sensor_dateto_temperature = currentDate;
              }
              else {
                sensor_dateto_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].date_to;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'manufacturer') {
              sensor_manufacturer_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'serial_number') {
              sensor_serial_number_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'data_sampling_interval') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_data_sampling_interval_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_data_sampling_interval_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_data_sampling_interval_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'accuracy') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_accuracy_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_accuracy_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_accuracy_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'height_diff_to_ant') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_height_diff_to_ant_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_height_diff_to_ant_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_height_diff_to_ant_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'calibration_date') {
              sensor_calibration_date_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'aspiration') {
              sensor_aspiration_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'distance_to_antenna') {
              sensor_distance_to_antenna_temperature = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
            }
          }
          temperatureSensorList.push({
            currentDate: currentDate,
            currentDateId: uuidv4(),
            datefrom: sensor_datefrom_temperature,
            datefromId: uuidv4(),
            dateto: sensor_dateto_temperature,
            datetoId: uuidv4(),
            sensor_model: sensor_model_temperature,
            sensor_model_id: uuidv4(),
            sensor_manufacturer: sensor_manufacturer_temperature,
            sensor_manufacturer_id: uuidv4(),
            sensor_serial_number: sensor_serial_number_temperature,
            sensor_serial_number_id: uuidv4(),
            sensor_data_sampling_interval: sensor_data_sampling_interval_temperature,
            sensor_data_sampling_interval_id: uuidv4(),
            sensor_accuracy: sensor_accuracy_temperature,
            sensor_accuracy_id: uuidv4(),
            sensor_height_diff_to_ant: sensor_height_diff_to_ant_temperature,
            sensor_height_diff_to_ant_id: uuidv4(),
            sensor_calibration_date: sensor_calibration_date_temperature,
            sensor_calibration_date_id: uuidv4(),
            sensor_aspiration: sensor_aspiration_temperature,
            sensor_aspiration_id: uuidv4(),
            sensor_distance_to_antenna: sensor_distance_to_antenna_temperature,
            sensor_distance_to_antenna_id: uuidv4(),
            comment: sensor_comment_temperature,
            commentId: uuidv4(),
          });
        }
        if (dataToDisplay[i].station_items[j].item.item_type.name === 'water_vapor_radiometer') {
          if (dataToDisplay[i].station_items[j].item.item_attributes[0].date_to === null) {
            sensor_dateto_radiometer = currentDate;
          }
          else {
            sensor_dateto_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[0].date_to;
          }
          sensor_comment_radiometer = dataToDisplay[i].station_items[j].item.comment;
          for (let k = 0; k < dataToDisplay[i].station_items[j].item.item_attributes.length; k += 1) {
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'water_vapor_radiometer') {
              sensor_water_vapor_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              sensor_datefrom_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].date_from;
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].date_to === null) {
                sensor_dateto_radiometer = currentDate;
              }
              else {
                sensor_dateto_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].date_to;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'manufacturer') {
              sensor_manufacturer_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'serial_number') {
              sensor_serial_number_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'height_diff_to_ant') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_height_diff_to_ant_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_height_diff_to_ant_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_height_diff_to_ant_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'calibration_date') {
              sensor_calibration_date_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'distance_to_antenna') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_distance_to_antenna_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_distance_to_antenna_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_distance_to_antenna_radiometer = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
          }
          waterVaporSensorlist.push({
            currentDate: currentDate,
            currentDateId: uuidv4(),
            datefrom: sensor_datefrom_radiometer,
            datefromId: uuidv4(),
            dateto: sensor_dateto_radiometer,
            datetoId: uuidv4(),
            sensor_manufacturer: sensor_manufacturer_radiometer,
            sensor_manufacturer_id: uuidv4(),
            sensor_serial_number: sensor_serial_number_radiometer,
            sensor_serial_number_id: uuidv4(),
            sensor_height_diff_to_ant: sensor_height_diff_to_ant_radiometer,
            sensor_height_diff_to_ant_id: uuidv4(),
            sensor_calibration_date: sensor_calibration_date_radiometer,
            sensor_calibration_date_id: uuidv4(),
            sensor_distance_to_antenna: sensor_distance_to_antenna_radiometer,
            sensor_distance_to_antenna_id: uuidv4(),
            sensor_water_vapor_radiometer: sensor_water_vapor_radiometer,
            sensor_water_vapor_id: uuidv4(),
            comment: sensor_comment_radiometer,
            commentId: uuidv4(),
          });
        }
        if (dataToDisplay[i].station_items[j].item.item_type.name === 'frequency_standard') {
          if (dataToDisplay[i].station_items[j].item.item_attributes[0].date_to === null) {
            sensor_dateto_frequency = currentDate;
          }
          else {
            sensor_dateto_frequency = dataToDisplay[i].station_items[j].item.item_attributes[0].date_to;
          }
          sensor_comment_frequency = dataToDisplay[i].station_items[j].item.comment;
          for (let k = 0; k < dataToDisplay[i].station_items[j].item.item_attributes.length; k += 1) {
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'standard_type') {
              sensor_standard_type_frequency = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              sensor_datefrom_frequency = dataToDisplay[i].station_items[j].item.item_attributes[k].date_from;
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].date_to === null) {
                sensor_dateto_frequency = currentDate;
              }
              else {
                sensor_dateto_frequency = dataToDisplay[i].station_items[j].item.item_attributes[k].date_to;
              }
            }
            if (dataToDisplay[i].station_items[j].item.item_attributes[k].attribute.name === 'input_frequency') {
              if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar !== null) {
                sensor_input_frequency_frequency = dataToDisplay[i].station_items[j].item.item_attributes[k].value_varchar;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric !== null) {
                sensor_input_frequency_frequency = dataToDisplay[i].station_items[j].item.item_attributes[k].value_numeric;
              }
              else if (dataToDisplay[i].station_items[j].item.item_attributes[k].value_date !== null) {
                sensor_input_frequency_frequency = dataToDisplay[i].station_items[j].item.item_attributes[k].value_date;
              }
            }
          }
          frequencyStandardSensorList.push({
            currentDate: currentDate,
            currentDateId: uuidv4(),
            datefrom: sensor_datefrom_frequency,
            datefromId: uuidv4(),
            dateto: sensor_dateto_frequency,
            datetoId: uuidv4(),
            sensor_standard_type: sensor_standard_type_frequency,
            sensor_standand_type_id: uuidv4(),
            sensor_input_frequency: sensor_input_frequency_frequency,
            sensor_input_frequency_id: uuidv4(),
            comment: sensor_comment_frequency,
            commentId: uuidv4(),
          });
        }
      }
    }
    dispatch(setSaveAntennaList(antennaList));
    dispatch(setSaveRadomeList(radomeList));
    dispatch(setSaveReceiverList(receiverList));
    dispatch(setSaveHumiditySensorList(humiditySensorList));
    dispatch(setSavePressureSensorList(pressureSensorList));
    dispatch(setSaveTemperatureSensorList(temperatureSensorList));
    dispatch(setSaveWaterVaporSensorList(waterVaporSensorlist));
    dispatch(setSaveFrequencyStandardSensorList(frequencyStandardSensorList));
    return arrTemp;
  }

  // when starting the page launch of the function allowing the data to be put in order and therefore displayed
  useEffect(() => {
    displayEquipment();
    const timeline = buildTimeline(recordsToInject, currentDateSessionView);
    dispatch(setSaveSessionViewData(timeline));
  }, []);

  return (
    <>
      <div className="metatitle"><span>Equipments</span></div>

      <ul className="nav nav-tabs" id="myTab" role="tablist">
        <li className="nav-item" role="presentation">
          <button className="nav-link active" id="sessionView-tab" data-bs-toggle="tab" data-bs-target="#sessionView-tab-pane" type="button" role="tab" aria-controls="sessionView-tab-pane" aria-selected="true">Instruments session view</button>
        </li>
        <li className="nav-item" role="presentation">
          <button className="nav-link" id="listView-tab" data-bs-toggle="tab" data-bs-target="#listView-tab-pane" type="button" role="tab" aria-controls="listView-tab-pane" aria-selected="true">Instruments list view</button>
        </li>
        <li className="nav-item" role="presentation">
          <button className="nav-link" id="other-tab" data-bs-toggle="tab" data-bs-target="#other-tab-pane" type="button" role="tab" aria-controls="other-tab-pane" aria-selected="true">Other instruments</button>
        </li>
      </ul>
      <div className="tab-content" id="myTabContent">
        {(sessionViewData.length > 0) && <SessionView sessionViewData={sessionViewData} />}
        {(sessionViewData.length > 0) && <ListView antennaList={antennaListState} receiverList={receiverListState} radomeList={radomeListState} frequencyStandardSensorList={frequencyStandardSensorListState} />}
        {(sessionViewData.length > 0) && <OtherInstruments frequencyStandardSensorList={frequencyStandardSensorListState} humiditySensorList={humiditySensorListState} pressureSensorList={pressureSensorListState} temperatureSensorList={temperatureSensorListState} waterVaporSensorList={waterVaporSensorlistState} />}
      </div>
    </>
  );
}

Instrumentation.propTypes = {
  fullData: PropTypes.arrayOf(
    PropTypes.shape,
  ).isRequired,
};

export default Instrumentation;
